import React, { useMemo } from "react";
import { Paper, Typography } from "@mui/material";
import { Contact } from "../../interfaces/DTO/crm/contact";
import { MimshakData } from "../../interfaces/DTO/mimshakData";
import { formatCurrency } from "../../utils/namesFormater";
import { MimshakDataUtil } from "../../services/mimshakDataUtil";
import { Product } from "../../interfaces/DTO/mislaka_xml_models";
import { FundDataBasic } from "../../interfaces/DTO/FundDataBasic";
import { LoanEligibilityConfigService } from "../../services/LoanEligibilityConfigService";
import { MimshakService } from "../../services/mimshakService";

interface SelfBankHeaderProps {
  name: string;
  contacts: Contact[];
  mimshakData: MimshakData[];
  fundData: FundDataBasic[];
}

const SelfBankHeader: React.FC<SelfBankHeaderProps> = ({
  name,
  contacts,
  mimshakData,
  fundData,
}) => {
  const isSingleContact = useMemo(() => contacts.length === 1, [contacts]);
  const products: Product[] = useMemo(
    () => MimshakDataUtil.extractAllProducts(mimshakData),
    [mimshakData]
  );

  const totalPotentialLoanAmount = useMemo(() => {
    return products.flatMap((product) =>
      product.policies_or_accounts.flatMap((policy) => {
        const mergedPaths = MimshakService.mergeDuplicateInvestmentPaths(policy.investment_paths);
        const futureSummaries = MimshakService.getFutureLiquiditySummaries([policy]);
        const alreadyLiquiedAmount = MimshakService.getPastLiquidityAmountSum([policy]);
        const isLiquid = alreadyLiquiedAmount > 0 || futureSummaries.length > 0;

        return mergedPaths?.map((path) => {
          const fund = fundData.find(f => f.fund_id === path.path_code);
          const sub = fund?.sub_specialization || "";
          const isStock = /מניות|סחיר/.test(sub);
          const percent = LoanEligibilityConfigService.getMaxLoanPercent(
            product.details.adjusted_product_type ? product.details.adjusted_product_type : product.details.product_type,
            isLiquid,
            isStock
          );
          return (path.accumulated_amount || 0) * percent;
        }) ?? [];
      })
    ).reduce((sum, amount) => sum + amount, 0);
  }, [products, fundData]);

  return (
    <Paper
      elevation={4}
      sx={{
        width: "100%",
        background: "linear-gradient(to right, #f3e5f5, #ede7f6)",
        p: 4,
        mb: 4,
        borderRadius: "10px",
        boxShadow: "0 4px 30px rgba(0,0,0,0.1)",
        textAlign: "center",
      }}
    >
      <Typography variant="h4" sx={{ mb: 2, color: "#6a1b9a" }}>
        {isSingleContact ? `הבנק של ${name}` : `בנק ${name}`}
      </Typography>

      <Typography variant="h6" sx={{ color: "#4527a0", mb: 1 }}>
        סכום הכסף בגינו ניתן לקחת הלוואה
      </Typography>

      <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32", mb: 1  }}>
        {formatCurrency(totalPotentialLoanAmount)}
      </Typography>
      <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 1 }}>
        הלוואות בתנאים מועדפים בלי למשוך את הכסף ולהמשיך להנות מתשואות
      </Typography>
       <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 3 }}>
        * הסכום הוא הערכה בלבד ואינו מהווה התחייבות למתן הלוואה
      </Typography>
    </Paper>
  );
};

export default SelfBankHeader;
